//@ts-nocheck
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { KR_V2_COLORS } from "./AstriaKoreaV2Card";
import { useKoreaLanguage } from "./koreaLanguage";
import { ls } from "../../utils/lnText";

// ─────────────────────────────────────────────────────────────────────────────
// ASTRIA KOREA V2 — Compatibility KR v2 result page.
// Scope: "Astria Korea V2" category, "Compatibility" subcategory ONLY.
// Fork of KoreaCompatibilityResult.tsx (v1), adapted to:
//   - send real categoryId/subCategoryId so the backend routes into
//     buildAstriaKoreaV2Context() / buildCompatibilityKRV2Prompt() instead of
//     v1's 3-box JSON-schema compatibility prompt
//   - read the structured astriaKoreaV2Data JSON the backend attaches on the
//     SSE `done` event (opening/bloodTypeReading/birthdayEnergyReading/
//     rhythmReading, see deriveCompatibilityV2DisplaySections() in
//     helper/AstriaKoreaV2Service.js) and render the client's 4-section
//     spec, styled to match AstriaKoreaV2Card.tsx's "compatibility" tab
//     palette. Self-contained (no shared card primitives imported) so it
//     stays resilient to AstriaKoreaV2Card.tsx redesigns — only KR_V2_COLORS
//     is shared:
//     Opening(오프닝) / Blood Type(혈액형) / Birthday Energy(생일 기운) /
//     Rhythm(리듬 조화).
// Does not modify or import from KoreaCompatibilityResult.tsx — zero impact
// on Astria Korea (v1) Compatibility.
// ─────────────────────────────────────────────────────────────────────────────

const KR_FONT =
  "'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";

const ACCENT = KR_V2_COLORS.compatibility;

const COLORS = {
  bg: KR_V2_COLORS.bg,
  cardBg: KR_V2_COLORS.cardBg,
  textDark: KR_V2_COLORS.textTitle,
  textMuted: KR_V2_COLORS.textMuted,
  textBody: KR_V2_COLORS.textBody,
  shadow: KR_V2_COLORS.shadow,
  shadowSoft: KR_V2_COLORS.shadowSoft,
};

// Translation object
const T = {
  korea: {
    // Loading
    loading: "두 사람의 궁합을 읽고 있어요...",

    // Header
    title: "두 사람의 궁합",
    subtitle: ls("Compatibility · Astria Korea V2"),

    // Sections — client fix-pack spec: 오프닝 / 혈액형 / 생일 기운 / 리듬 조화
    sections: {
      opening: "오프닝",
      bloodTypeReading: "혈액형",
      birthdayEnergyReading: "생일 기운",
      rhythmReading: "리듬 조화",
    },

    // Buttons
    back: "돌아가기",

    // Error
    error: "궁합 분석 중 오류가 발생했습니다. 다시 시도해주세요.",

    // Fallback
    fallback: "분석 내용을 준비하고 있어요...",

    // Icons (keep emojis as they're universal)
    icons: {
      opening: "💎",
      bloodTypeReading: "🩸",
      birthdayEnergyReading: "🎂",
      rhythmReading: "🎐",
    },
  },
  global: {
    // Loading
    loading: ls("Reading the compatibility between two people..."),

    // Header
    title: ls("Compatibility Reading"),
    subtitle: ls("Compatibility · Astria Korea V2"),

    // Sections
    sections: {
      opening: "Opening",
      bloodTypeReading: "Blood Type",
      birthdayEnergyReading: "Birthday Energy",
      rhythmReading: "Rhythm",
    },

    // Buttons
    back: "Back",

    // Error
    error: ls("An error occurred during compatibility analysis. Please try again."),

    // Fallback
    fallback: "Preparing analysis content...",

    // Icons (keep emojis as they're universal)
    icons: {
      opening: "💎",
      bloodTypeReading: "🩸",
      birthdayEnergyReading: "🎂",
      rhythmReading: "🎐",
    },
  },
};

// Section definitions with translations — client fix-pack spec: 4 sections,
// each a plain-text reading (Opening / Blood Type / Birthday Energy / Rhythm)
const getSectionDefs = (t: typeof T.korea) =>
  [
    { key: "opening", title: t.sections.opening, icon: t.icons.opening },
    {
      key: "bloodTypeReading",
      title: t.sections.bloodTypeReading,
      icon: t.icons.bloodTypeReading,
    },
    {
      key: "birthdayEnergyReading",
      title: t.sections.birthdayEnergyReading,
      icon: t.icons.birthdayEnergyReading,
    },
    {
      key: "rhythmReading",
      title: t.sections.rhythmReading,
      icon: t.icons.rhythmReading,
    },
  ] as const;

function getScoreColor(score: number): string {
  if (score >= 80) return "#7BA9A0";
  if (score >= 60) return "#A9C08B";
  if (score >= 40) return "#D9C08A";
  if (score >= 20) return "#D9A98A";
  return "#D99B9B";
}

interface KoreaV2CompatibilityResultProps {
  selfData: any;
  partnerData: any;
  categoryId: string;
  subCategoryId: string;
  onBack?: () => void;
  // True when the active category is "Astria Korea V3", which always
  // replies in Korean regardless of user/region language (see
  // KR_V3_LANGUAGE_RULE in AstriaKoreaV3Service.js). When set, UI copy is
  // pinned to Korean so labels never mismatch the AI's Korean response —
  // V2 keeps its existing region-driven behavior untouched.
  forceKorean?: boolean;
}

const KoreaV2CompatibilityResult: React.FC<KoreaV2CompatibilityResultProps> = ({
  selfData,
  partnerData,
  categoryId,
  subCategoryId,
  onBack,
  forceKorean,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rawContent, setRawContent] = useState("");
  const [structuredData, setStructuredData] = useState<any>(null);

  // Recomputed on every mount (not at module load) so it reflects the
  // region set on login — localStorage.userRegion is only populated after
  // AuthContext runs, which happens after this module first evaluates.
  // Korean UI when the user's preferred language is "ko", global (English)
  // otherwise — see koreaLanguage.ts.
  const REGION: "korea" | "global" =
    useKoreaLanguage() === "ko" ? "korea" : "global";
  const IS_KOREA = forceKorean || REGION === "korea";
  const t = IS_KOREA ? T.korea : T.global;
  const subtitle = forceKorean
    ? IS_KOREA
      ? "궁합 · Astria Korea V3"
      : "Compatibility · Astria Korea V3"
    : t.subtitle;

  useEffect(() => {
    fetchResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchResult = async () => {
    setLoading(true);
    setError("");

    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");

      // Build labels based on region
      const nameLabel = IS_KOREA ? "이름" : "Name";
      const bloodLabel = IS_KOREA ? "혈액형" : "Blood Type";
      const dobLabel = IS_KOREA ? "생년월일" : "Date of Birth";
      const timeLabel = IS_KOREA ? "시간" : "Time";
      const birthPlaceLabel = IS_KOREA ? "출생지" : "Birth Place";

      const selfSummary = [
        selfData.name && `${nameLabel}: ${selfData.name}`,
        selfData.blood_type && `${bloodLabel}: ${selfData.blood_type}`,
        selfData.dob && `${dobLabel}: ${selfData.dob}`,
        selfData.destiny_time && `${timeLabel}: ${selfData.destiny_time}`,
        selfData.birth_city && `${birthPlaceLabel}: ${selfData.birth_city}`,
      ]
        .filter(Boolean)
        .join(", ");

      const partnerSummary = [
        partnerData.name && `${nameLabel}: ${partnerData.name}`,
        partnerData.blood_type && `${bloodLabel}: ${partnerData.blood_type}`,
        partnerData.dob && `${dobLabel}: ${partnerData.dob}`,
        partnerData.destiny_time && `${timeLabel}: ${partnerData.destiny_time}`,
        partnerData.birth_city &&
          `${birthPlaceLabel}: ${partnerData.birth_city}`,
      ]
        .filter(Boolean)
        .join(", ");

      // Build message in appropriate language
      let message;
      if (IS_KOREA) {
        message = `두 사람의 궁합을 읽어주세요.\n나 (사용자): ${selfSummary}\n상대 (파트너): ${partnerSummary}`;
      } else {
        message = `Please read the compatibility between two people.\nI (User): ${selfSummary}\nPartner: ${partnerSummary}`;
      }

      const baseUrl = api.defaults.baseURL || "";
      const streamUrl = `${baseUrl}/backend/chat/create?stream=true`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      // Real categoryId/subCategoryId are required so the backend resolves
      // categoryName === "Astria Korea V2" and routes into
      // buildAstriaKoreaV2Context() -> buildCompatibilityKRV2Prompt() instead
      // of v1's hasKorea3BoxData fallback path.
      const requestData: any = {
        userMessage: message,
        categoryId,
        subCategoryId,
        userId: userId || undefined,
        korea3BoxSelf: selfData,
        korea3BoxPartner: partnerData,
        saveChat: false,
        // Pass region to backend
        region: REGION,
      };

      const response = await fetch(streamUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });

      if (!response.ok || !response.body) {
        throw new Error("Stream request failed");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullResponse = "";
      let doneData: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") break;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.done) {
              doneData = parsed;
            } else if (parsed.text) {
              fullResponse += parsed.text;
            } else if (parsed.content || parsed.content_text) {
              fullResponse += parsed.content || parsed.content_text;
            } else if (parsed.choices && parsed.choices[0]?.delta?.content) {
              fullResponse += parsed.choices[0].delta.content;
            }
          } catch {
            // Ignore parsing errors for individual chunks
          }
        }
      }

      setRawContent(fullResponse.trim());
      setStructuredData(doneData?.astriaKoreaV2Data || null);
    } catch (err: any) {
      console.error("Korea V2 compatibility error:", err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const hasStructuredSections = !!structuredData;
  const sectionDefs = getSectionDefs(t);

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          fontFamily: KR_FONT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: 18 }}>
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                animate={{ y: [0, -8, 0], opacity: [0.4, 1, 0.4] }}
                transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  delay: i * 0.18,
                  ease: "easeInOut",
                }}
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: ACCENT.light,
                  margin: "0 4px",
                }}
              />
            ))}
          </div>
          <p
            style={{
              fontSize: 14,
              color: COLORS.textMuted,
              fontFamily: KR_FONT,
            }}
          >
            {t.loading}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: COLORS.bg,
          fontFamily: KR_FONT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", padding: "0 20px" }}>
          <p
            style={{
              fontSize: 14,
              color: COLORS.textMuted,
              marginBottom: 20,
              fontFamily: KR_FONT,
            }}
          >
            {error}
          </p>
          <motion.button
            whileTap={{ scale: 0.97 }}
            whileHover={{ y: -1 }}
            onClick={() => {
              if (onBack) onBack();
              else navigate(-1);
            }}
            style={{
              padding: "12px 24px",
              borderRadius: 999,
              background: ACCENT.primary,
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: KR_FONT,
              border: "none",
              cursor: "pointer",
              boxShadow: COLORS.shadow,
            }}
          >
            {t.back}
          </motion.button>
        </div>
      </div>
    );
  }

  // Main result page
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        minHeight: "100vh",
        background: `${ACCENT.gradient}, ${COLORS.bg}`,
        fontFamily: KR_FONT,
        padding: "40px 20px 48px",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 8 }}>✨</div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: COLORS.textDark,
              lineHeight: 1.3,
              letterSpacing: "-0.02em",
              fontFamily: "'Nanum Myeongjo', 'Noto Serif KR', serif",
            }}
          >
            {t.title}
          </div>
          <div
            style={{ fontSize: 13.5, color: COLORS.textMuted, marginTop: 4 }}
          >
            {subtitle}
          </div>
        </div>

        {/* Sections */}
        {hasStructuredSections ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {sectionDefs.map((def, index) => {
              const isOpening = def.key === "opening";
              const sectionText = structuredData?.[def.key];
              if (!sectionText) return null;

              return (
                <motion.div
                  key={def.key}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -2 }}
                  transition={{
                    delay: 0.08 * index,
                    duration: 0.4,
                    ease: [0.23, 1, 0.32, 1],
                  }}
                  style={{
                    background: COLORS.cardBg,
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    borderRadius: 16,
                    padding: "20px 22px",
                    boxShadow: COLORS.shadowSoft,
                    border: `1px solid ${ACCENT.primary}15`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 10,
                    }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>
                      {def.icon}
                    </span>
                    <h3
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: COLORS.textDark,
                        margin: 0,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {def.title}
                    </h3>
                  </div>

                  <div
                    style={{
                      fontSize: 14.5,
                      fontWeight: 400,
                      lineHeight: 1.8,
                      color: COLORS.textBody,
                    }}
                  >
                    <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {sectionText}
                    </p>
                    {isOpening && typeof structuredData.score === "number" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                          marginTop: 16,
                          background: `${ACCENT.primary}06`,
                          padding: "12px 16px",
                          borderRadius: 12,
                          border: `1px solid ${ACCENT.primary}10`,
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            width: 46,
                            height: 46,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#fff",
                            background: getScoreColor(structuredData.score),
                            flexShrink: 0,
                            boxShadow: `0 4px 12px ${getScoreColor(structuredData.score)}30`,
                          }}
                        >
                          {Math.round(structuredData.score)}
                        </div>
                        {structuredData.tone && (
                          <span
                            style={{
                              fontSize: 13,
                              color: COLORS.textMuted,
                              fontStyle: "italic",
                              flex: 1,
                            }}
                          >
                            {structuredData.tone}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              background: COLORS.cardBg,
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderRadius: 16,
              padding: "20px 22px",
              fontSize: 15,
              lineHeight: 1.8,
              color: COLORS.textBody,
              fontFamily: KR_FONT,
              whiteSpace: "pre-wrap",
              border: `1px solid ${ACCENT.primary}12`,
              boxShadow: COLORS.shadowSoft,
            }}
          >
            {rawContent || t.fallback}
          </div>
        )}

        {/* Back Button */}
        <div style={{ textAlign: "center", marginTop: 36 }}>
          <motion.button
            whileTap={{ scale: 0.97 }}
            whileHover={{ y: -1 }}
            onClick={() => {
              if (onBack) onBack();
              else navigate(-1);
            }}
            style={{
              padding: "14px 34px",
              borderRadius: 999,
              background: ACCENT.primary,
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              fontFamily: KR_FONT,
              border: "none",
              cursor: "pointer",
              boxShadow: COLORS.shadow,
            }}
          >
            {t.back}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default KoreaV2CompatibilityResult;
